import { useCallback, useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { OccurrenceRow } from "@/components/teacher/schedule/calendar/OccurrenceRow";
import type { WeeklyItem, ClassTemplateEvent } from "@/types/schedule";
import { useTeacherProjectedSchedule } from "@/hooks/useTeacherProjectedSchedule";

function dateISO(d: Date) { return d.toISOString().slice(0,10); }



export const UpcomingNext7: React.FC<{ teacherCode?: string }> = ({ teacherCode }) => {
  const [loadingEvents, setLoadingEvents] = useState(false);
  const [events, setEvents] = useState<ClassTemplateEvent[]>([]);

  const today = useMemo(()=> new Date(), []);
  const rangeStartISO = dateISO(today);
  const rangeEndISO = useMemo(() => {
    const d = new Date(today);
    d.setDate(d.getDate() + 6);
    return dateISO(d);
  }, [today]);

  const projectedQuery = useTeacherProjectedSchedule(teacherCode, rangeStartISO, rangeEndISO);
  const items = (projectedQuery.data?.items ?? []) as WeeklyItem[];

  const fetchData = useCallback(async () => {
    if (!teacherCode) { setEvents([]); return; }
    setLoadingEvents(true);
    const { data } = await supabase
      .from('v_class_template_events')
      .select('*')
      .eq('requested_by', teacherCode)
      .gte('event_date', rangeStartISO);
    setEvents((data ?? []) as ClassTemplateEvent[]);
    setLoadingEvents(false);
  }, [teacherCode, rangeStartISO]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (!teacherCode) return <div className="text-muted-foreground">กรุณากรอกรหัสครูในข้อมูลส่วนตัว</div>;
  if (projectedQuery.isLoading || loadingEvents) return <div className="text-muted-foreground">กำลังโหลด...</div>;
  if (projectedQuery.isError) return <div className="text-muted-foreground">โหลดข้อมูลไม่สำเร็จ</div>;
  if (items.length === 0) return <div className="text-muted-foreground">ไม่มีคาบใน 7 วันถัดไป</div>;

  const grouped = items.reduce<Record<string, WeeklyItem[]>>((acc, it)=>{
    (acc[it.date] ||= []).push(it);
    return acc;
  }, {});

  return (
    <div className="space-y-4">
      {Object.entries(grouped).map(([date, arr]) => (
        <div key={date} className="rounded border">
          <div className="px-4 py-2 font-semibold bg-muted">{new Date(date).toLocaleDateString('th-TH')}</div>
          <div className="p-4 space-y-2">
            {arr.sort((a,b)=>a.start_time.localeCompare(b.start_time)).map(item => (
              <OccurrenceRow
                key={`${item.fixed_job_id}-${item.date}`}
                item={item}
                teacherCode={teacherCode}
                events={events}
                onRefresh={fetchData}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};
